/**
 * @file ServiceMachineDetailPage.jsx
 * @description SERVICE-focused machine detail — opened from the Service page's
 *              TCU Machines list (distinct from the rental machine view).
 *              Sections, all bound to real data from
 *              GET /maintenance/machine/:equipmentId/overview:
 *                1. identity header (model, serial, status, engine hours)
 *                2. Last Service + Next Maintenance Due
 *                3. Maintenance to-do — the due checklist's items (expandable
 *                   step-by-step) + the full A–H interval schedule
 *                4. DTC fault codes the machine itself reported (NOT alerts)
 *                5. Service history — every ticket with its action log
 *                   ("how we solved it")
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Chip, Button, CircularProgress, Alert,
  Breadcrumbs, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Collapse, Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Build as ServiceIcon,
  AccessTimeFilled as HoursIcon,
  Badge as SerialIcon,
  PrecisionManufacturing as MachineIcon,
  ReportProblem as DtcIcon,
  History as HistoryIcon,
  Checklist as ChecklistIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  FiberManualRecord as DotIcon,
  EventRepeat as ScheduleIcon,
} from '@mui/icons-material';
import maintenanceService from '../../services/maintenanceService';
import { statusLabel, statusColor } from '../../utils/statusLabels';

const A = { teal: '#0f766e', blue: '#2563eb', amber: '#d97706', red: '#dc2626', green: '#16a34a', slate: '#475569', cyan: '#0891b2' };

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};
const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
};
const fmtNum = (v, frac = 1) => (v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: frac }));
const nice = (s) => String(s || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

const TICKET_STATUS = {
  OPEN: '#dc2626', ASSIGNED: '#d97706', IN_PROGRESS: '#2563eb',
  WAITING_SPARES: '#7c3aed', RESOLVED: '#16a34a', CLOSED: '#64748b', CANCELLED: '#94a3b8',
};

const SectionCard = ({ title, icon, accent = A.slate, right, children, sx }) => (
  <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, borderBottom: '1px solid', borderColor: 'divider', background: `${accent}0d` }}>
      <Box sx={{ color: accent, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
      {right && <Box sx={{ ml: 'auto' }}>{right}</Box>}
    </Box>
    <Box sx={{ p: 2.5, flex: 1 }}>{children}</Box>
  </Paper>
);

const KVRow = ({ label, value, last }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderBottom: last ? 'none' : '1px solid', borderColor: 'rgba(148,163,184,0.14)' }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right' }}>{value || '—'}</Typography>
  </Box>
);

// One maintenance to-do item, with its step-by-step process collapsible.
const TodoItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  const hasSteps = Array.isArray(item.steps) && item.steps.length > 0;
  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'rgba(148,163,184,0.14)', py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: hasSteps ? 'pointer' : 'default' }}
        onClick={() => hasSteps && setOpen(!open)}>
        <Chip size="small" label={item.code} sx={{ fontFamily: 'monospace', fontWeight: 700, height: 20, bgcolor: '#f1f5f9' }} />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{item.title}</Typography>
        {item.type && <Chip size="small" label={item.type} variant="outlined" sx={{ height: 20, textTransform: 'capitalize' }} />}
        {hasSteps && (open ? <CollapseIcon fontSize="small" color="disabled" /> : <ExpandIcon fontSize="small" color="disabled" />)}
      </Box>
      {hasSteps && (
        <Collapse in={open}>
          <Box component="ol" sx={{ m: 0, mt: 1, pl: 4 }}>
            {item.steps.map((s, i) => (
              <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{s}</Typography>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// One service ticket row + its action log ("how we solved it").
const TicketRow = ({ t }) => {
  const [open, setOpen] = useState(false);
  const color = TICKET_STATUS[String(t.status || '').toUpperCase()] || A.slate;
  const hasLogs = (t.logs || []).length > 0;
  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', '& td': { borderBottom: open ? 'none' : undefined } }} onClick={() => setOpen(!open)}>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(t.opened_at)}</TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={700}>{nice(t.issue_type)}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>{t.service_id}</Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 340 }}>
          <Typography variant="body2" color="text.secondary" noWrap>{t.description || '—'}</Typography>
        </TableCell>
        <TableCell align="center">
          <Chip size="small" label={nice(t.priority)} sx={{ height: 20, fontWeight: 700 }} variant="outlined" />
        </TableCell>
        <TableCell align="center">
          <Chip size="small" label={nice(t.status)} sx={{ height: 20, fontWeight: 700, bgcolor: `${color}15`, color }} />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.completed_at ? fmtDate(t.completed_at) : '—'}</TableCell>
        <TableCell align="right" sx={{ color: 'text.disabled' }}>
          {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open}>
            <Box sx={{ py: 1.5, pl: 1 }}>
              {hasLogs ? (
                <>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>HOW IT WAS SOLVED</Typography>
                  {t.logs.map((l, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.25, mb: 1 }}>
                      <DotIcon sx={{ fontSize: 10, mt: 0.75, color: A.green }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{l.action_taken}</Typography>
                        <Typography variant="caption" color="text.disabled">
                          {fmtDateTime(l.logged_at)}{l.remarks ? ` · ${l.remarks}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              ) : (
                <Typography variant="body2" color="text.disabled">No action logged yet{String(t.status).toUpperCase() === 'OPEN' ? ' — ticket is still open.' : '.'}</Typography>
              )}
              {t.notes && <Typography variant="caption" color="text.secondary">Notes: {t.notes}</Typography>}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * `embedded` renders this same service view inside another page — the machine
 * detail page shows it behind its "Details" button — taking the machine from
 * the `equipmentId` prop instead of the URL, and dropping the breadcrumbs,
 * back button and identity header, which the host page already shows.
 */
export default function ServiceMachineDetailPage({ embedded = false, equipmentId: equipmentIdProp = null }) {
  const { equipmentId: equipmentIdParam } = useParams();
  const equipmentId = equipmentIdProp || equipmentIdParam;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await maintenanceService.getMachineServiceOverview(equipmentId);
      setData(res);
    } catch (e) {
      setError(e.message || 'Failed to load service details.');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);
  useEffect(() => { load(); }, [load]);

  const goBack = () => navigate('/service');

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (error) {
    return (
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        {!embedded && (
          <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 2 }}>Back to Service</Button>
        )}
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      </Box>
    );
  }

  const m = data?.machine || {};
  const last = data?.last_service || null;
  const next = data?.next_service || null;
  const schedule = data?.schedule || [];
  const dtcs = data?.dtcs || [];
  const tickets = data?.tickets || [];
  const title = [m.brand, m.model].filter(Boolean).join(' ') || m.equipment_id || 'Machine';
  const activeDtcs = dtcs.filter((d) => d.active === 1 || d.active === true).length;

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 }, maxWidth: embedded ? 'none' : 1280, mx: 'auto' }}>
      {/* breadcrumbs + back — the host page has its own when embedded */}
      {!embedded && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Breadcrumbs>
          <Link component="button" underline="hover" color="inherit" onClick={goBack}>Service</Link>
          <Typography color="text.secondary">Machine</Typography>
          <Typography color="text.primary" fontWeight={700} sx={{ fontFamily: 'monospace' }}>{m.serial_number || m.equipment_id}</Typography>
        </Breadcrumbs>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} size="small">Back to Service</Button>
      </Box>
      )}

      {/* identity header — skipped when embedded: the host page's own header
          already names the machine, and two stacked identity cards read badly */}
      {!embedded && (
      <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', p: { xs: 2.5, md: 3 }, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: `${A.amber}15`, color: A.amber, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ServiceIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1 }}>{title}</Typography>
            {m.category && (
              <Typography variant="body2" color="text.secondary">{m.category}</Typography>
            )}
          </Box>
          {m.status && <Chip label={statusLabel(m.status)} color={statusColor(m.status)} sx={{ fontWeight: 700 }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <SerialIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>{m.serial_number || '—'}</Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <HoursIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="body2" fontWeight={700}>{m.engine_hours != null ? `${fmtNum(m.engine_hours)} h` : 'no meter'}</Typography>
          </Box>
          {m.last_communication && (
            <Typography variant="body2" color="text.disabled">Last data {fmtDateTime(m.last_communication)} IST</Typography>
          )}
        </Box>
      </Paper>
      )}

      {/* last service + next due */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Last Service" icon={<ServiceIcon />} accent={A.amber}>
            {last ? (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1 }}>{nice(last.issue_type)}</Typography>
                <KVRow label="Ticket" value={last.service_id} />
                <KVRow label="Completed" value={fmtDate(last.completed_at || last.opened_at)} />
                <KVRow label="Status" value={nice(last.status)} />
                {last.description && <KVRow label="Work" value={last.description} last />}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">No completed service on record for this machine.</Typography>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title="Next Maintenance Due" icon={<ScheduleIcon />} accent={A.teal}
            right={next ? <Chip size="small" label={`in ${fmtNum(next.hours_until)} h`} sx={{ bgcolor: next.hours_until <= 10 ? '#dc2626' : A.teal, color: '#fff', fontWeight: 700 }} /> : null}>
            {next ? (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1 }}>
                  Service {next.checklist} · at {fmtNum(next.next_at, 0)} h
                </Typography>
                <KVRow label="Interval" value={next.interval_desc || `Every ${next.interval} h`} />
                <KVRow label="Machine meter now" value={m.engine_hours != null ? `${fmtNum(m.engine_hours)} h` : '—'} />
                <KVRow label="Checklist items" value={`${(next.items || []).length} tasks`} last />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No meter reading from this machine yet — the service schedule is computed from engine hours.
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* maintenance to-do (the due checklist's items) + full schedule */}
      {next && (
        <SectionCard title={`Maintenance To-Do — Service ${next.checklist}`} icon={<ChecklistIcon />} accent={A.blue}
          right={<Typography variant="caption" color="text.disabled">{next.interval_desc}</Typography>} sx={{ mb: 2.5 }}>
          {(next.items || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No checklist items on file for this service.</Typography>
          ) : (
            <Box sx={{ mb: 2 }}>
              {next.items.map((it) => <TodoItem key={it.code} item={it} />)}
            </Box>
          )}
          {schedule.length > 1 && (
            <>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>FULL SERVICE SCHEDULE</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {schedule.map((s) => (
                  <Tooltip key={s.checklist} title={s.interval_desc || `Every ${s.interval} h`}>
                    <Chip size="small"
                      label={`${s.checklist} · at ${fmtNum(s.next_at, 0)} h (in ${fmtNum(s.hours_until)} h)`}
                      sx={{ fontWeight: 600, bgcolor: s.checklist === next.checklist ? `${A.blue}15` : '#f8fafc', color: s.checklist === next.checklist ? A.blue : 'text.secondary' }} />
                  </Tooltip>
                ))}
              </Box>
            </>
          )}
        </SectionCard>
      )}

      {/* DTC fault codes (device-reported; NOT the alerts feed) */}
      <SectionCard title="DTC Fault Codes" icon={<DtcIcon />} accent={A.red}
        right={<Chip size="small" label={`${activeDtcs} active`} sx={{ bgcolor: activeDtcs ? A.red : '#e2e8f0', color: activeDtcs ? '#fff' : 'text.secondary', fontWeight: 700 }} />}
        sx={{ mb: 2.5 }}>
        {dtcs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No DTC fault codes received from this machine.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Received</TableCell>
                  <TableCell align="center">SPN</TableCell>
                  <TableCell align="center">FMI</TableCell>
                  <TableCell>Failure mode</TableCell>
                  <TableCell>Code / Description</TableCell>
                  <TableCell align="center">Count</TableCell>
                  <TableCell align="center">State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dtcs.map((d, i) => (
                  <TableRow key={i} hover sx={(d.active === 1 || d.active === true) ? { bgcolor: '#dc262608' } : undefined}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(d.received_at)}</TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{d.spn ?? '—'}</TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{d.fmi ?? '—'}</TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{d.fmi_text || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{d.description || d.code || '—'}</Typography>
                    </TableCell>
                    <TableCell align="center">{d.occurrence_count ?? '—'}</TableCell>
                    <TableCell align="center">
                      {(d.active === 1 || d.active === true)
                        ? <Chip size="small" label="Active" sx={{ height: 20, fontWeight: 700, bgcolor: A.red, color: '#fff' }} />
                        : <Chip size="small" label="Cleared" sx={{ height: 20, fontWeight: 700, bgcolor: '#f1f5f9', color: 'text.secondary' }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      {/* service history + how each was solved */}
      <SectionCard title="Service History" icon={<HistoryIcon />} accent={A.slate}
        right={<Chip size="small" label={tickets.length} sx={{ bgcolor: A.slate, color: '#fff', fontWeight: 700 }} />}>
        {tickets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No service tickets for this machine yet.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Opened</TableCell>
                  <TableCell>Type / Ticket</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Priority</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((t) => <TicketRow key={t.id} t={t} />)}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Box>
  );
}
